import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  GraduationCap,
  UploadCloud,
  SendHorizontal,
  CheckCircle as CheckCircleIcon,
  Rocket,
  Scissors,
  Loader,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { RootState } from '../../../reducers';
import usePlan from '../../plan/hooks/plan';
import { useUserTags } from '../../../hooks/useTags';
import { GlobalDialogProps } from '../../../types';
import { config } from '../../../../config';
import logger from '../../../lib/logger';
import { GEMINI_MODEL } from '../../microtools/common';
import { pushModal } from '../../globalModals/globalModalsSlice';

// List of known education domain endings
const EDUCATION_DOMAINS = [
  '.edu',
  '.ac.uk',
  '.edu.au',
  '.ac.nz',
  '.edu.cn',
  '.edu.hk',
  '.ac.jp',
  '.edu.sg',
  '.ac.za',
  '.edu.mx',
  '.edu.br',
  // Add more as needed
];

interface EducationalDiscountDialogProps extends GlobalDialogProps {
  formSource?: string;
  onClose: () => void;
}

// Dialog component for educational discount verification
const EducationalDiscountDialog: React.FC<EducationalDiscountDialogProps> = ({
  onClose,
  formSource,
}) => {
  const { t } = useTranslation();
  const { settings } = useSelector((state: RootState) => state);
  const userEmail = settings.user.email || '';
  const { setTag } = useUserTags();
  const dispatch = useDispatch();

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageVerificationStatus, setImageVerificationStatus] = useState<
    'idle' | 'loading' | 'verified' | 'failed'
  >('idle');
  const [verificationMessage, setVerificationMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if email domain is an educational institution
  const isEducationalEmail = EDUCATION_DOMAINS.some((domain) =>
    userEmail.toLowerCase().endsWith(domain),
  );

  const handleVerifyByEmail = () => {
    // Set the user tag for educational discount
    setTag({ key: 'educationalDiscount', value: 'verified' });
    setImageVerificationStatus('verified');
    setVerificationMessage(
      'Your email has been verified as an educational email. You are eligible for the educational discount!',
    );
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setSelectedImage(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleUpgradeWithDiscount = () => {
    // Close the current dialog
    onClose();

    // Add the promo code to the URL
    const url = window.location.pathname;
    const hasParams = window.location.search.length > 0;
    const newUrl = hasParams
      ? `${url}${window.location.search}&promo=EDU50`
      : `${url}?promo=EDU50`;

    window.history.replaceState({}, document.title, newUrl);

    // Open the upgrade modal directly
    dispatch(
      pushModal({
        type: 'stripe-pricing-table-upgrade-modal',
        source: 'educational-discount',
      }),
    );
  };

  const handleVerifyByImage = async () => {
    if (!selectedImage || imageVerificationStatus === 'loading') return;

    setImageVerificationStatus('loading');

    try {
      // Use Gemini API to verify if the uploaded image is an educational ID or document
      logger.info('Attempting to verify educational document with Gemini API', {
        formSource,
      });

      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  inlineData: {
                    mimeType: 'image/jpeg',
                    data: selectedImage.split(',')[1], // Remove the data URL prefix
                  },
                },
                {
                  text: "Is this a valid educational ID, student card, or educational institution document? Please just respond with 'YES' if it is educational, or 'NO' if it's not clearly educational. If YES, briefly note what educational institution or document type it is.",
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        logger.error('API error response', {
          status: response.status,
          statusText: response.statusText,
        });
        throw new Error(
          `API Error: ${response.status} - ${response.statusText}`,
        );
      }

      const data = await response.json();

      if (data.candidates && data.candidates[0]) {
        const aiResponse = data.candidates[0].content.parts[0].text;

        // Check if the AI detected it as an educational document
        if (aiResponse.toLowerCase().startsWith('yes')) {
          // Set verification status and tag for educational discount
          logger.info('Document verified as educational');
          setImageVerificationStatus('verified');
          setVerificationMessage(
            `Verification successful! ${aiResponse.substring(3)}`,
          );
          setTag({ key: 'educationalDiscount', value: 'verified' });
        } else {
          logger.info('Document not verified as educational', { aiResponse });
          setImageVerificationStatus('failed');
          setVerificationMessage(
            'The uploaded image does not appear to be a valid educational document. Please try again with a clearer image of your student ID or educational document.',
          );
        }
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (error) {
      logger.error('Error verifying educational document', error);
      setImageVerificationStatus('failed');
      setVerificationMessage(
        'An error occurred while verifying your document. Please try again later.',
      );
    }
  };

  // If email is from an educational domain, automatically verify
  useEffect(() => {
    if (isEducationalEmail) {
      handleVerifyByEmail();
    }
  }, [isEducationalEmail]);

  // Message text based on email type
  const getEmailMessage = () => {
    if (isEducationalEmail) {
      return t('upgrade.educational.emailDetected');
    }
    return t('upgrade.educational.verifyYourEducation');
  };

  // Render the success view
  const renderSuccessView = () => (
    <div className="text-center max-w-md mx-auto">
      <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-green-600 mb-2">
        {t('upgrade.educational.verificationSuccess')}
      </h3>
      <p className="text-base mb-2">
        {t('upgrade.educational.verificationSuccessMessage')}
      </p>
      <p className="text-sm text-muted-foreground mb-6">
        {t('upgrade.educational.accessibilityMessage')}
      </p>
      <Button onClick={handleUpgradeWithDiscount} size="lg" className="mb-6">
        <Rocket className="mr-2 h-4 w-4" />
        {t('upgrade.educational.upgradeWithDiscount')}
      </Button>
      <p className="text-xs text-muted-foreground mb-2">
        {t('upgrade.educational.saveCodeLater')}
      </p>
      <div className="relative inline-block border border-dashed rounded px-4 py-2 mb-2">
        <span className="font-medium">EDU50</span>
        <Scissors className="absolute -top-2 right-2 w-4 h-4 text-muted-foreground" />
      </div>
    </div>
  );

  // Render the educational email verification
  const renderEmailVerification = () => (
    <Button onClick={handleVerifyByEmail} size="lg">
      {t('upgrade.educational.applyDiscount')}
    </Button>
  );

  // Render the image verification
  const renderImageVerification = () => (
    <div className="w-full flex flex-col items-center">
      <img
        src={selectedImage || ''}
        alt="Uploaded verification document"
        className="w-full h-44 object-contain mb-4"
      />

      <Button
        onClick={handleVerifyByImage}
        disabled={imageVerificationStatus === 'loading'}
        size="lg"
      >
        {imageVerificationStatus === 'loading' ? (
          <Loader className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <SendHorizontal className="mr-2 h-4 w-4" />
        )}
        {imageVerificationStatus === 'loading'
          ? t('upgrade.educational.verifying')
          : t('upgrade.educational.verifyDocument')}
      </Button>

      {imageVerificationStatus === 'failed' && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{verificationMessage}</AlertDescription>
        </Alert>
      )}
    </div>
  );

  // Render the image upload
  const renderImageUpload = () => (
    <>
      <input
        type="file"
        accept="image/*"
        hidden
        ref={fileInputRef}
        onChange={handleImageUpload}
      />
      <Button onClick={() => fileInputRef.current?.click()} size="lg">
        <UploadCloud className="mr-2 h-4 w-4" />
        {t('upgrade.educational.uploadVerification')}
      </Button>

      <p className="text-xs text-muted-foreground mt-6">
        {t('upgrade.educational.documentTypes')}
      </p>
    </>
  );

  // Determine which verification UI to render
  const renderVerificationUI = () => {
    if (isEducationalEmail) {
      return renderEmailVerification();
    }

    if (selectedImage) {
      return renderImageVerification();
    }

    return renderImageUpload();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('upgrade.educational.title')}</DialogTitle>
        </DialogHeader>
        <div className="p-6 min-h-[250px] flex flex-col items-center">
          {imageVerificationStatus === 'verified' ? (
            renderSuccessView()
          ) : (
            <div className="text-center max-w-md mx-auto">
              <h3 className="text-lg font-semibold mb-2">
                {t('upgrade.educational.subtitle')}
              </h3>

              <p className="text-base mb-6">
                {t('upgrade.educational.description')}
                {getEmailMessage()}
              </p>

              {renderVerificationUI()}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main component for the educational discount button
const EducationalDiscount = () => {
  const { t } = useTranslation();
  const { plan } = useSelector((state: RootState) => state);
  const { getTag } = useUserTags();
  const { isPlan } = usePlan();
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);

  const userGoal = getTag('userGoal');
  const hasEducationGoal = userGoal === 'education';
  const isFreePlan =
    !plan.subscription?.package ||
    plan.subscription.package === 'FREE_LOGGEDIN';
  const upgraded = isPlan(['GROWTH', 'BUSINESS', 'ENTERPRISE']);

  // Only show for free plan users with education goal who haven't upgraded
  if (!isFreePlan || !hasEducationGoal || upgraded) {
    return null;
  }

  const handleClick = () => {
    setDiscountDialogOpen(true);
  };

  const handleDiscountDialogClose = () => {
    setDiscountDialogOpen(false);
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="default"
              onClick={handleClick}
              className="h-10"
            >
              <GraduationCap className="mr-2 h-4 w-4" />
              {t('upgrade.buttons.getDiscount').replace(/ /g, '\u00A0')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('upgrade.tooltips.educational')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {discountDialogOpen && (
        <EducationalDiscountDialog
          onClose={handleDiscountDialogClose}
          formSource="Dashboard - Educational Discount"
        />
      )}
    </>
  );
};

export default EducationalDiscount;
